import React, {
  createContext,
  useContext,
  ReactNode,
  useEffect,
  useState,
} from "react";
import {
  BankConnectionMetaImportProgressEnum,
  buildBankConnectionsApiWithToken,
} from "@bn/api/apiv3";
import { useLocalStorage } from "react-use";
import { useQueryClient } from "@tanstack/react-query";
import { Account, buildAccountsApiWithToken } from "@bn/api/apiv1";
import { BankStatus } from "@providers/BankImportStatusProvider/BankStatus.tsx";
import { GET_ACCOUNTS } from "@api/cacheKeys";
import { useBankImportParams } from "./useBankImportParams";

type BankImportStatusContextType = {
  status?: BankConnectionMetaImportProgressEnum | null;
  accounts?: (Account & {
    referenceAccount?: Account;
  })[];
  closeMessage: () => void;
  type: "import" | "update" | undefined;
};

const BankImportStatusContext = createContext<
  BankImportStatusContextType | undefined
>(undefined);

const BANK_CONNECTION_IN_PROGRESS_KEY = "wealthAPI-bankConnectionInProgress";

const MAX_POLL_FAILURES = 3;

export const BankImportStatusProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [
    wealthAPIBankConnection,
    setWealthAPIBankConnection,
    removeWealthAPIBankConnection,
  ] = useLocalStorage<{
    id: number;
    type: "import" | "update";
  } | null>(BANK_CONNECTION_IN_PROGRESS_KEY, null);
  const { bankConnectionId, bankConnectionType, flow, clearParams } =
    useBankImportParams();
  const queryClient = useQueryClient();

  const [showMessage, setShowMessage] = useState(false);
  const [accounts, setAccounts] = useState<
    (Account & {
      referenceAccount?: Account;
    })[]
  >();
  const [status, setStatus] =
    useState<BankConnectionMetaImportProgressEnum | null>();

  useEffect(() => {
    if (!bankConnectionId) return;

    // The progress below comes from the v3 API, which has no finAPI connections
    if (bankConnectionType === "WEALTH_API") {
      setWealthAPIBankConnection({
        id: bankConnectionId,
        type: flow === "update" ? "update" : "import",
      });
    }

    clearParams();
  }, [
    bankConnectionId,
    bankConnectionType,
    flow,
    clearParams,
    setWealthAPIBankConnection,
  ]);

  const closeMessage = () => {
    setShowMessage(false);
    setStatus(null);
    setAccounts([]);
    removeWealthAPIBankConnection();
  };

  useEffect(() => {
    if (!wealthAPIBankConnection?.id) return;

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let failures = 0;
    let reachedSuccess = false;

    const scheduleNextTick = () => {
      const timeout = import.meta.env.WEALTH_API_IMPORT_CHECK_INTERVAL
        ? parseInt(import.meta.env.WEALTH_API_IMPORT_CHECK_INTERVAL)
        : 1500;
      timeoutId = setTimeout(poll, timeout);
    };

    const tick = async () => {
      if (cancelled) return;

      const api = await buildBankConnectionsApiWithToken();
      const accountsApi = await buildAccountsApiWithToken();
      const response = await api.getBankConnection({
        id: Number(wealthAPIBankConnection.id),
      });
      if (cancelled) return;

      const bcData = response.data.data;
      failures = 0;
      setStatus(bcData.meta.import_progress);
      setShowMessage(true);

      if (bcData.meta.import_progress === "SUCCESS") {
        reachedSuccess = true;

        // A finished Smartbroker+ import or update restores the premium role,
        // so the roles cached since login are now stale. Refetching here is
        // what lets the expiry notice disappear without a reload.
        queryClient.invalidateQueries({ queryKey: ["myself"] });
        queryClient.invalidateQueries({ queryKey: [GET_ACCOUNTS] });

        const accountsResponse = await accountsApi.listAndSearchAllAccounts({
          bankConnectionId: [Number(bcData.id)],
        });
        if (cancelled) return;

        const filteredAccounts = accountsResponse.data.accounts?.filter(
          (account) => {
            return account.depotIds?.length
              ? !accountsResponse.data.accounts?.some((a) =>
                  account.depotIds?.includes(a.id)
                )
              : true;
          }
        );
        const groupedAccounts = filteredAccounts?.map((account) => {
          const referenceAccount = accountsResponse.data.accounts?.find(
            (a) => a.id === account.contraAccountId
          );
          if (!referenceAccount) return account;
          return { ...account, referenceAccount };
        });
        if (groupedAccounts) setAccounts(groupedAccounts);
        return;
      }

      if (bcData.meta.import_progress === "FAILED_WITH_ERROR") return;

      scheduleNextTick();
    };

    const poll = () =>
      tick().catch(() => {
        if (cancelled) return;

        failures += 1;
        if (failures <= MAX_POLL_FAILURES) {
          scheduleNextTick();
          return;
        }

        // The import itself did finish; only the account list is missing
        if (reachedSuccess) return;

        setStatus(BankConnectionMetaImportProgressEnum.FAILED_WITH_ERROR);
        setShowMessage(true);
      });

    poll();

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [wealthAPIBankConnection, queryClient]);

  return (
    <BankImportStatusContext.Provider
      value={{
        status,
        type: wealthAPIBankConnection?.type,
        accounts,
        closeMessage,
      }}
    >
      {children}
      {showMessage && <BankStatus />}
    </BankImportStatusContext.Provider>
  );
};

export const useBankImportContext = (): BankImportStatusContextType => {
  const context = useContext(BankImportStatusContext);
  if (!context) {
    throw new Error(
      "useBankImportStatus must be used within a BankImportStatusProvider"
    );
  }
  return context;
};
